import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, Info, Pencil, Plus, RefreshCw, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";
import { apiErrorMessage } from "@/lib/apiError";

const R = (n) => {
  const [whole, decimal] = Number(n || 0).toFixed(2).split(".");
  return `R ${Number(whole).toLocaleString("en-US")}.${decimal}`;
};
const KIND_LABEL = { monthly: "Monthly", weekly: "Weekly", one_off: "One-off" };
const RECURRING = new Set(["monthly", "weekly"]);
// Quick picks: common running costs a small business pays. Name → category.
const QUICK_PICKS = [
  ["Rent", "Rent"], ["Electricity", "Utilities"], ["Water", "Utilities"], ["Internet", "Utilities"],
  ["Salaries", "Salaries"], ["Insurance", "Insurance"], ["Card machine fees", "Bank & fees"], ["Tax", "Tax"],
];
const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Session 31.5 — Reports → Expenses & Profit.
 * Owner-defined monthly / one-off expenses, cost of goods, and an exact
 * break-even view for a month or a week. All maths happens on the server
 * (/api/owner/reports/profit) — this panel only presents it.
 */
export const ExpensesProfitPanel = ({ http, authHeaders, onSendToSpreadsheet }) => {
  const [period, setPeriod] = useState("month");
  const [anchor, setAnchor] = useState(() => isoDate(new Date()));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({ profit_basis: "excl_vat", cogs_basis: "item_cost", paid_only: true });
  const [dialog, setDialog] = useState({ open: false, id: null, name: "", amount: "", kind: "monthly", category: "Other", start_month: monthKey(new Date()), end_month: "", date: isoDate(new Date()), notes: "" });
  const [saving, setSaving] = useState(false);
  const [showDays, setShowDays] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [r, e] = await Promise.all([
        http.get("/owner/reports/profit", { headers: authHeaders, params: { period, anchor } }),
        http.get("/owner/expenses", { headers: authHeaders }),
      ]);
      setReport(r.data);
      setExpenses(e.data?.expenses || []);
      setCategories(e.data?.categories || []);
      setSettings(e.data?.settings || settings);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the profit report."));
    } finally {
      setLoading(false);
    }
  }, [http, authHeaders, period, anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const shift = (dir) => {
    const d = new Date(`${anchor}T00:00:00`);
    if (period === "month") { d.setDate(1); d.setMonth(d.getMonth() + dir); } else { d.setDate(d.getDate() + 7 * dir); }
    setAnchor(isoDate(d));
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      await http.put("/owner/expenses/settings", next, { headers: authHeaders });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save settings.");
    }
  };

  const openNew = () => setDialog({ open: true, id: null, name: "", amount: "", kind: "monthly", category: "Other", start_month: monthKey(new Date(`${anchor}T00:00:00`)), end_month: "", date: anchor, notes: "" });
  const openEdit = (e) => setDialog({ open: true, id: e.id, name: e.name, amount: String(e.amount), kind: e.kind, category: e.category || "Other", start_month: e.start_month || monthKey(new Date()), end_month: e.end_month || "", date: e.date || anchor, notes: e.notes || "" });

  const submit = async () => {
    const amount = Number(dialog.amount);
    if (!dialog.name.trim()) { toast.error("Give the expense a name."); return; }
    if (!Number.isFinite(amount) || amount < 0) { toast.error("Enter a valid amount."); return; }
    setSaving(true);
    try {
      const body = { name: dialog.name.trim(), amount, kind: dialog.kind, category: dialog.category || "Other", notes: dialog.notes || null };
      if (RECURRING.has(dialog.kind)) { body.start_month = dialog.start_month || null; body.end_month = dialog.end_month || null; }
      else body.date = dialog.date || null;
      if (dialog.id) {
        if (RECURRING.has(dialog.kind) && !dialog.end_month) body.clear_end_month = true;
        await http.put(`/owner/expenses/${dialog.id}`, body, { headers: authHeaders });
      } else {
        await http.post("/owner/expenses", body, { headers: authHeaders });
      }
      toast.success(dialog.id ? "Expense updated." : "Expense added.");
      setDialog((d) => ({ ...d, open: false }));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the expense.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    try {
      await http.delete(`/owner/expenses/${e.id}`, { headers: authHeaders });
      toast.success(`Removed ${e.name}.`);
      setConfirmDelete(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove the expense.");
    }
  };

  const onDialogKeyDown = (ev) => {
    if (ev.key === "Enter" && !saving && ev.target?.tagName === "INPUT") { ev.preventDefault(); submit(); }
  };

  const toggleActive = async (e) => {
    try {
      await http.put(`/owner/expenses/${e.id}`, { active: !e.active }, { headers: authHeaders });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the expense.");
    }
  };

  const be = report?.break_even;
  const profitPositive = (report?.profit ?? 0) >= 0;
  const inPeriodIds = useMemo(() => new Set((report?.expenses?.lines || []).map((l) => l.id)), [report]);

  if (loading && !report) {
    return <div className="space-y-3" data-testid="profit-loading"><Skeleton className="h-24 w-full rounded-[24px]" /><Skeleton className="h-40 w-full rounded-[24px]" /></div>;
  }
  if (error && !report) {
    return <Card className="rounded-[24px]"><CardContent className="p-6 text-sm text-destructive" data-testid="profit-error">{error} <Button variant="outline" size="sm" className="ml-2 rounded-xl" onClick={load}>Retry</Button></CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="expenses-profit-panel">
      {/* Period controls */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl border border-border bg-card p-1">
            {["month", "week"].map((p) => (
              <button key={p} type="button" onClick={() => setPeriod(p)} className={`h-9 rounded-lg px-3 text-sm font-medium ${period === p ? "bg-[hsl(var(--primary))] text-primary-foreground" : "hover:bg-secondary"}`} data-testid={`profit-period-${p}`}>{p === "month" ? "Monthly" : "Weekly"}</button>
            ))}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => shift(-1)} aria-label="Previous period" data-testid="profit-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <p className="min-w-[120px] whitespace-nowrap text-center text-sm font-semibold" data-testid="profit-period-label">{report?.period?.label}</p>
            <Button variant="outline" size="icon" className="h-9 w-9 rounded-xl" onClick={() => shift(1)} aria-label="Next period" data-testid="profit-next"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl" onClick={load} aria-label="Refresh" data-testid="profit-refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {onSendToSpreadsheet ? <Button variant="outline" size="sm" className="rounded-xl" onClick={() => onSendToSpreadsheet(report)} data-testid="profit-send-to-spreadsheet">Send to spreadsheet</Button> : null}
          <Button size="sm" className="rounded-xl" onClick={openNew} data-testid="expense-add"><Plus className="h-4 w-4" /> Add expense</Button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Card className="rounded-[24px]"><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Net sales</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums" data-testid="profit-net-sales">{R(report?.sales?.net)}</p>
          <p className="mt-1 text-xs text-muted-foreground">{report?.sales?.orders} orders · gross {R(report?.sales?.gross)}{report?.sales?.refunds ? ` · refunds −${R(report.sales.refunds)}` : ""}{settings.profit_basis === "excl_vat" ? ` · VAT −${R(report?.sales?.vat)}` : ""}</p>
        </CardContent></Card>
        <Card className="rounded-[24px]"><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Cost of goods</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums" data-testid="profit-cogs">−{R(report?.cost_of_goods?.total)}</p>
          <p className="mt-1 text-xs text-muted-foreground">{settings.cogs_basis === "purchases" ? "Stock received × cost" : report?.cost_of_goods?.coverage_pct != null ? `${report.cost_of_goods.coverage_pct}% of sales are costed items` : "No costed items yet"}</p>
        </CardContent></Card>
        <Card className="rounded-[24px]"><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">Expenses</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums" data-testid="profit-expenses">−{R(report?.expenses?.total)}</p>
          <p className="mt-1 text-xs text-muted-foreground">{(report?.expenses?.lines || []).length} item{(report?.expenses?.lines || []).length === 1 ? "" : "s"}{period === "week" ? " · monthly costs counted day-by-day" : " · weekly costs × weeks in month"}</p>
        </CardContent></Card>
        <Card className={`rounded-[24px] border ${profitPositive ? "border-emerald-300/60 dark:border-emerald-800/60" : "border-rose-300/60 dark:border-rose-800/60"}`}><CardContent className="p-4">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">{profitPositive ? "Profit" : "Loss"}</p>
          <p className={`mt-1 flex items-center gap-2 text-2xl font-semibold tabular-nums ${profitPositive ? "text-emerald-700 dark:text-emerald-300" : "text-rose-700 dark:text-rose-300"}`} data-testid="profit-total">
            {profitPositive ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}{R(Math.abs(report?.profit || 0))}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Net sales − cost of goods − expenses</p>
        </CardContent></Card>
      </div>

      {/* Break-even */}
      <Card className="rounded-[24px]"><CardContent className="space-y-3 p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold">Break-even</p>
          <p className="text-sm tabular-nums" data-testid="profit-break-even-text">
            {be?.target > 0
              ? (be.remaining > 0.005 ? <>Still <span className="font-semibold">{R(be.remaining)}</span> of costs to cover before profit</> : <>All costs covered{be.reached_on ? ` on ${new Date(`${be.reached_on}T00:00:00`).toLocaleDateString("en-ZA", { day: "numeric", month: "short" })}` : ""} — everything since is profit</>)
              : "No expenses recorded for this period — add your monthly costs to see break-even"}
          </p>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-secondary" aria-hidden="true">
          <div className={`h-full rounded-full ${be?.pct >= 100 ? "bg-emerald-500" : "bg-[hsl(var(--accent))]"}`} style={{ width: `${Math.min(100, Math.max(0, be?.pct || 0))}%` }} data-testid="profit-break-even-bar" />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
          <span>{R(be?.covered)} of {R(be?.target)} covered ({be?.pct ?? 0}%) · gross margin {R(report?.gross_margin)}</span>
          {be?.projected_profit != null && report?.period?.days_elapsed < report?.period?.days_total ? (
            <span data-testid="profit-projection">At today's pace this {period} ends at {be.projected_profit >= 0 ? "a profit of" : "a loss of"} <span className="font-medium">{R(Math.abs(be.projected_profit))}</span></span>
          ) : null}
        </div>
      </CardContent></Card>

      {/* Data quality hint */}
      {settings.cogs_basis === "item_cost" && report?.cost_of_goods?.coverage_pct != null && report.cost_of_goods.coverage_pct < 90 ? (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-300/70 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-100" data-testid="profit-costing-warning">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>Only {report.cost_of_goods.coverage_pct}% of sales come from items with a cost. Add a <strong>cost price</strong> to menu items (Menu → edit item) or a <strong>cost per unit</strong> to ingredients (Inventory → Ingredients) so cost of goods is complete — {report.cost_of_goods.menu_items.uncosted} item{report.cost_of_goods.menu_items.uncosted === 1 ? "" : "s"} still uncosted{report.cost_of_goods.ingredients_without_cost?.length ? `; ingredients without a cost: ${report.cost_of_goods.ingredients_without_cost.slice(0, 5).join(", ")}${report.cost_of_goods.ingredients_without_cost.length > 5 ? "…" : ""}` : ""}.</span>
        </div>
      ) : null}

      <div className="grid min-w-0 gap-4 lg:grid-cols-5">
        {/* Expense list */}
        <Card className="min-w-0 rounded-[24px] lg:col-span-3"><CardContent className="min-w-0 space-y-3 p-5">
          <div className="flex flex-wrap items-center justify-between gap-1">
            <p className="text-sm font-semibold">Your expenses</p>
            <p className="text-xs text-muted-foreground">Monthly and weekly items repeat until you end them</p>
          </div>
          {expenses.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="expenses-empty">
              No expenses yet. Add rent, water, lights, tax, salaries — anything you pay to keep the doors open.
              <div className="mt-3"><Button size="sm" className="rounded-xl" onClick={openNew}><Plus className="h-4 w-4" /> Add your first expense</Button></div>
            </div>
          ) : (
            <>
            {/* Mobile: one card per expense (table columns don't fit at 390px). */}
            <div className="space-y-2 sm:hidden" data-testid="expenses-mobile-list">
              {expenses.map((e) => {
                const line = (report?.expenses?.lines || []).find((l) => l.id === e.id);
                return (
                  <div key={e.id} className={`rounded-2xl border border-border p-3 ${!e.active ? "opacity-60" : ""}`} data-testid={`expense-card-${e.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate font-medium">{e.name}</p>
                        <p className="text-xs text-muted-foreground">{e.category}{RECURRING.has(e.kind) ? ` · from ${e.start_month}${e.end_month ? ` to ${e.end_month}` : ""}` : ` · ${e.date}`}</p>
                      </div>
                      <Badge variant="outline" className="shrink-0 rounded-full">{KIND_LABEL[e.kind] || "Monthly"}</Badge>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <div className="min-w-0 text-sm tabular-nums">
                        {line ? <p className="font-medium">{R(line.amount_in_period)} <span className="font-normal text-muted-foreground">this {period}</span></p> : <p className="text-xs text-muted-foreground">Not in this {period}</p>}
                        {RECURRING.has(e.kind) ? <p className="text-xs text-muted-foreground">{R(e.amount)} per {e.kind === "weekly" ? "week" : "month"}</p> : null}
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        <Switch checked={e.active} onCheckedChange={() => toggleActive(e)} aria-label="Active" />
                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg" onClick={() => openEdit(e)} aria-label="Edit"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg text-destructive hover:text-destructive" onClick={() => setConfirmDelete(e)} aria-label="Delete"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hidden overflow-x-auto rounded-2xl border border-border sm:block">
              <Table>
                <TableHeader><TableRow><TableHead>Expense</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">This {period}</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {expenses.map((e) => {
                    const line = (report?.expenses?.lines || []).find((l) => l.id === e.id);
                    return (
                      <TableRow key={e.id} className={!e.active ? "opacity-60" : ""} data-testid={`expense-row-${e.id}`}>
                        <TableCell>
                          <p className="font-medium">{e.name}</p>
                          <p className="text-xs text-muted-foreground">{e.category}{RECURRING.has(e.kind) ? ` · from ${e.start_month}${e.end_month ? ` to ${e.end_month}` : ""}` : ` · ${e.date}`}{e.notes ? ` · ${e.notes}` : ""}</p>
                        </TableCell>
                        <TableCell><Badge variant="outline" className="rounded-full">{KIND_LABEL[e.kind] || "Monthly"}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">{R(e.amount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{line ? <>{R(line.amount_in_period)}{line.prorated ? <span className="ml-1 text-[10px] text-muted-foreground">{e.kind === "weekly" ? `(${(line.amount_in_period / (e.amount || 1)).toFixed(1)} wks)` : "(share)"}</span> : null}</> : <span className="text-xs text-muted-foreground">{inPeriodIds.has(e.id) ? "" : "not in period"}</span>}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex items-center gap-1">
                            <Switch checked={e.active} onCheckedChange={() => toggleActive(e)} aria-label="Active" data-testid={`expense-active-${e.id}`} />
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => openEdit(e)} aria-label="Edit" data-testid={`expense-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg text-destructive hover:text-destructive" onClick={() => setConfirmDelete(e)} aria-label="Delete" data-testid={`expense-delete-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            </>
          )}
          {Object.keys(report?.expenses?.by_category || {}).length ? (
            <div className="flex flex-wrap gap-1.5" data-testid="expenses-by-category">
              {Object.entries(report.expenses.by_category).map(([k, v]) => <Badge key={k} variant="secondary" className="rounded-full font-normal">{k}: {R(v)}</Badge>)}
            </div>
          ) : null}
        </CardContent></Card>

        {/* Settings + top items */}
        <div className="min-w-0 space-y-4 lg:col-span-2">
          <Card className="rounded-[24px]"><CardContent className="space-y-3 p-5">
            <p className="flex items-center gap-1.5 text-sm font-semibold"><Info className="h-4 w-4 text-muted-foreground" /> How profit is calculated</p>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Take VAT out of sales first<span className="block text-xs text-muted-foreground">VAT isn't yours to keep — recommended if you're VAT-registered.</span></span>
              <Switch checked={settings.profit_basis === "excl_vat"} onCheckedChange={(v) => saveSettings({ profit_basis: v ? "excl_vat" : "incl_vat" })} data-testid="profit-setting-vat" />
            </label>
            <label className="flex items-center justify-between gap-3 text-sm">
              <span>Count paid orders only<span className="block text-xs text-muted-foreground">Off = every non-voided order, including unpaid tabs.</span></span>
              <Switch checked={!!settings.paid_only} onCheckedChange={(v) => saveSettings({ paid_only: v })} data-testid="profit-setting-paid" />
            </label>
            <div className="space-y-1">
              <Label className="text-xs">Cost of goods from</Label>
              <Select value={settings.cogs_basis} onValueChange={(v) => saveSettings({ cogs_basis: v })}>
                <SelectTrigger className="h-10 rounded-xl" data-testid="profit-setting-cogs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="item_cost">Item costs (recipes / cost price × sold)</SelectItem>
                  <SelectItem value="purchases">Stock purchases (received × cost per unit)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-[11px] text-muted-foreground">Tips are never counted as sales. Periods follow the business timezone ({report?.period?.timezone}).</p>
          </CardContent></Card>

          {report?.cost_of_goods?.top_items?.length ? (
            <Card className="rounded-[24px]"><CardContent className="space-y-2 p-5">
              <p className="text-sm font-semibold">Margin by item (costed items)</p>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Sold</TableHead><TableHead className="text-right">Sales</TableHead><TableHead className="text-right">Margin</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {report.cost_of_goods.top_items.map((it) => (
                      <TableRow key={it.name}><TableCell className="text-sm">{it.name}</TableCell><TableCell className="text-right tabular-nums text-sm">{it.quantity}</TableCell><TableCell className="text-right tabular-nums text-sm">{R(it.revenue)}</TableCell><TableCell className={`text-right tabular-nums text-sm ${it.margin < 0 ? "text-rose-600" : ""}`}>{R(it.margin)}</TableCell></TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent></Card>
          ) : null}
        </div>
      </div>

      {/* Daily breakdown */}
      <Card className="rounded-[24px]"><CardContent className="space-y-2 p-5">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold">Day by day</p>
          <Button variant="ghost" size="sm" className="rounded-xl" onClick={() => setShowDays((v) => !v)} data-testid="profit-days-toggle">{showDays ? "Hide" : "Show"}</Button>
        </div>
        {showDays ? (
          <div className="overflow-x-auto rounded-2xl border border-border">
            <Table>
              <TableHeader><TableRow><TableHead>Date</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Net sales</TableHead><TableHead className="text-right">Cost of goods</TableHead><TableHead className="text-right">Margin</TableHead><TableHead className="text-right">Running total vs expenses</TableHead></TableRow></TableHeader>
              <TableBody>
                {(report?.days || []).map((d) => {
                  const over = d.cumulative_margin - (report?.expenses?.total || 0);
                  return (
                    <TableRow key={d.date} className={d.future ? "opacity-50" : ""} data-testid={`profit-day-${d.date}`}>
                      <TableCell className="text-sm">{new Date(`${d.date}T00:00:00`).toLocaleDateString("en-ZA", { weekday: "short", day: "numeric", month: "short" })}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{d.orders}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{R(d.net_sales)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{R(d.cost_of_goods)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{R(d.margin)}</TableCell>
                      <TableCell className={`text-right tabular-nums text-sm ${over >= 0 ? "text-emerald-700 dark:text-emerald-300" : "text-muted-foreground"}`}>{over >= 0 ? `+${R(over)} profit` : `${R(Math.abs(over))} to go`}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : null}
      </CardContent></Card>

      {/* Add / edit dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="rounded-[24px] sm:max-w-md" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{dialog.id ? "Edit expense" : "Add an expense"}</DialogTitle>
            <DialogDescription>Monthly and weekly expenses repeat until you end them; one-off expenses count only in the period they were paid.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3" onKeyDown={onDialogKeyDown}>
            {!dialog.id ? (
              <div className="flex flex-wrap gap-1.5" data-testid="expense-quick-picks">
                {QUICK_PICKS.map(([name, cat]) => (
                  <button key={name} type="button" onClick={() => setDialog((d) => ({ ...d, name, category: cat }))} className={`h-7 rounded-full border px-2.5 text-xs font-medium transition-colors ${dialog.name === name ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-primary-foreground" : "border-border bg-card hover:bg-secondary"}`} data-testid={`expense-quick-${name.toLowerCase().replace(/\s+/g, "-")}`}>{name}</button>
                ))}
              </div>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1"><Label htmlFor="exp-name">Name</Label><Input id="exp-name" value={dialog.name} onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))} placeholder="e.g. Electricity" className="h-10 rounded-xl" data-testid="expense-name" /></div>
              <div className="space-y-1"><Label htmlFor="exp-amount">{dialog.kind === "weekly" ? "Amount per week (R)" : dialog.kind === "monthly" ? "Amount per month (R)" : "Amount (R)"}</Label><Input id="exp-amount" type="number" min="0" step="0.01" value={dialog.amount} onChange={(e) => setDialog((d) => ({ ...d, amount: e.target.value }))} placeholder="3000" className="h-10 rounded-xl" data-testid="expense-amount" /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Type</Label>
                <Select value={dialog.kind} onValueChange={(v) => setDialog((d) => ({ ...d, kind: v }))}>
                  <SelectTrigger className="h-10 rounded-xl" data-testid="expense-kind"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Monthly (repeats)</SelectItem><SelectItem value="weekly">Weekly (repeats)</SelectItem><SelectItem value="one_off">One-off</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={dialog.category} onValueChange={(v) => setDialog((d) => ({ ...d, category: v }))}>
                  <SelectTrigger className="h-10 rounded-xl" data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{(categories.length ? categories : ["Other"]).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {RECURRING.has(dialog.kind) ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1"><Label htmlFor="exp-start">From month</Label><Input id="exp-start" type="month" value={dialog.start_month} onChange={(e) => setDialog((d) => ({ ...d, start_month: e.target.value }))} className="h-10 rounded-xl" data-testid="expense-start-month" /></div>
                <div className="space-y-1"><Label htmlFor="exp-end">Until month (optional)</Label><Input id="exp-end" type="month" value={dialog.end_month} onChange={(e) => setDialog((d) => ({ ...d, end_month: e.target.value }))} className="h-10 rounded-xl" data-testid="expense-end-month" /></div>
              </div>
            ) : (
              <div className="space-y-1"><Label htmlFor="exp-date">Paid on</Label><Input id="exp-date" type="date" value={dialog.date} onChange={(e) => setDialog((d) => ({ ...d, date: e.target.value }))} className="h-10 rounded-xl" data-testid="expense-date" /></div>
            )}
            {dialog.kind === "weekly" ? <p className="text-[11px] text-muted-foreground" data-testid="expense-weekly-hint">Counted day-exact: a 30-day month = 30 ÷ 7 = 4.29 weeks of this amount.</p> : null}
            <div className="space-y-1"><Label htmlFor="exp-notes">Notes (optional)</Label><Input id="exp-notes" value={dialog.notes} onChange={(e) => setDialog((d) => ({ ...d, notes: e.target.value }))} placeholder="e.g. Eskom account 1234" className="h-10 rounded-xl" data-testid="expense-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDialog((d) => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="rounded-xl" onClick={submit} disabled={saving} data-testid="expense-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-[24px]" data-testid="expense-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {RECURRING.has(confirmDelete?.kind)
                ? "This removes it from every month's profit report, including past months. If it simply stopped, edit it and set an \"Until month\" instead."
                : "This one-off expense will no longer count towards the period it was paid in."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="expense-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => confirmDelete && remove(confirmDelete)} data-testid="expense-delete-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ExpensesProfitPanel;
